import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import {
  AuthStore,
  ErrorAlertComponent,
  FeatureHeaderComponent,
  PERMISSIONS,
  SelectInputComponent,
  SubmitButtonComponent,
  TabsNavigationComponent,
  TextInputComponent,
} from '@sandbox-platform/shared-ui';
import type { SelectOption } from '@sandbox-platform/shared-ui';

import { CommissionSettingsDetailStore } from './+state/commission-settings-detail.store';
import {
  CommissionSettingsDTO,
  FISCAL_MONTH_OPTIONS,
  PAYOUT_FREQUENCY_OPTIONS,
  UpdateCommissionSettingsDTO,
} from '../../../types/commission-settings.types';

@Component({
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TextInputComponent,
    SelectInputComponent,
    SubmitButtonComponent,
    ErrorAlertComponent,
    FeatureHeaderComponent,
    TabsNavigationComponent,
  ],
  selector: 'lib-commission-settings-detail',
  templateUrl: './commission-settings-detail.component.html',
})
export class CommissionSettingsDetailComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);
  protected readonly authStore = inject(AuthStore);
  readonly store = inject(CommissionSettingsDetailStore);

  readonly tabs = [
    { id: 'payout', label: 'Payout' },
    { id: 'retainer', label: 'Retainer' },
    { id: 'notifications', label: 'Notifications' },
  ];
  readonly activeTab = signal<string>('payout');

  readonly payoutFrequencyOptions: SelectOption[] = PAYOUT_FREQUENCY_OPTIONS;
  readonly fiscalMonthOptions: SelectOption[] = FISCAL_MONTH_OPTIONS.map(
    (o) => ({
      ...o,
      value: String(o.value),
    })
  );

  readonly form = this.fb.group({
    payout_frequency: ['monthly'],
    payout_threshold_amount: [0],
    default_currency: ['KES'],
    fiscal_year_start_month: ['1'],
    retainer_min_duration_months: [6],
    notify_on_commission_created: [false],
    notify_on_commission_paid: [false],
    approval_required_above_amount: [null as number | null],
  });

  protected readonly canRead = computed(() =>
    this.authStore.hasExpertPermission(
      PERMISSIONS.EXPERT.COMMISSION_SETTINGS_READ
    )
  );
  protected readonly canUpdate = computed(() =>
    this.authStore.hasExpertPermission(
      PERMISSIONS.EXPERT.COMMISSION_SETTINGS_UPDATE
    )
  );
  protected readonly isFormDirty = computed(() => this.form.dirty);

  async ngOnInit(): Promise<void> {
    if (!this.canRead()) {
      return;
    }
    const settings = await this.store.get();
    if (settings) {
      this.populateForm(settings);
    }
  }

  onTabChange(tabId: string): void {
    this.activeTab.set(tabId);
  }

  onNotifyOnCreatedChange(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.form.get('notify_on_commission_created')?.setValue(checked);
    this.form.markAsDirty();
  }

  onNotifyOnPaidChange(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.form.get('notify_on_commission_paid')?.setValue(checked);
    this.form.markAsDirty();
  }

  async onSave(): Promise<void> {
    if (!this.canUpdate() || !this.form.dirty) {
      return;
    }

    const v = this.form.getRawValue();
    const dto: UpdateCommissionSettingsDTO = {
      payout_frequency:
        v.payout_frequency as UpdateCommissionSettingsDTO['payout_frequency'],
      payout_threshold_amount: Number(v.payout_threshold_amount ?? 0),
      default_currency: (v.default_currency ?? 'KES').toUpperCase(),
      fiscal_year_start_month: Number(v.fiscal_year_start_month ?? 1),
      retainer_min_duration_months: Number(v.retainer_min_duration_months ?? 6),
      notify_on_commission_created: v.notify_on_commission_created ?? false,
      notify_on_commission_paid: v.notify_on_commission_paid ?? false,
    };

    const approvalAmount = Number(v.approval_required_above_amount);
    if (!isNaN(approvalAmount) && approvalAmount > 0) {
      dto.approval_required_above_amount = approvalAmount;
    }

    const result = await this.store.update(dto);
    if (result) {
      this.populateForm(result);
    }
  }

  goBack(): void {
    this.router.navigate(['/dashboard']);
  }

  private populateForm(settings: CommissionSettingsDTO): void {
    this.form.patchValue({
      payout_frequency: settings.payout_frequency,
      payout_threshold_amount: settings.payout_threshold_amount,
      default_currency: settings.default_currency,
      fiscal_year_start_month: String(settings.fiscal_year_start_month),
      retainer_min_duration_months: settings.retainer_min_duration_months,
      notify_on_commission_created: settings.notify_on_commission_created,
      notify_on_commission_paid: settings.notify_on_commission_paid,
      approval_required_above_amount:
        settings.approval_required_above_amount ?? null,
    });
    this.form.markAsPristine();
  }
}
